/**
 * `unityBasis.ts` — the ONE canonical Unity ↔ three.js/glTF basis change, plus
 * the Unity rotation math it needs, as pure tuples.
 *
 * Pure by necessity: `src/models/**` may not import `three`
 * (`Campfire_Web_General` §4).
 *
 * Two thin `Vector3`/`Quaternion` adapters sit over this module in
 * `src/runtime/assets/`, and there are two of them on purpose — they name the
 * two conversions that are genuinely different and that a call site must choose
 * between:
 *
 * - `snapshotRotationConversion.ts` — the NODE-FRAME conversion, for entity
 *   snapshots and anything else whose local axes mean the same thing in both
 *   spaces. Uses {@link convertBasisQuaternion}.
 * - `participantTransformConversion.ts` — the WORLD-POSE conversion, for
 *   participants, cameras, cards and markers. Uses
 *   {@link convertWorldPoseQuaternion}.
 *
 * Both delegate here; neither re-derives. Nothing anywhere else may spell a
 * basis conversion out by hand — the bug this module exists to end was eight
 * hand-written copies that drifted apart.
 *
 * ## The map
 *
 * Unity is left-handed (+X right, +Y up, +Z forward); glTF and three.js are
 * right-handed (+Z toward the viewer). The dictionary between them is
 *
 *   `M = diag(-1, 1, 1)` — negate X — with **determinant -1**.
 *
 * The determinant is the whole point, and getting it wrong is what task 0004
 * is about: a basis change between opposite handednesses MUST be a reflection.
 * Any composite that comes out at det +1 is a rotation, and a rotation cannot
 * carry left-handed content into a right-handed renderer — it renders the scene
 * mirrored. {@link UNITY_BASIS_DETERMINANT} exists so a harness can assert this
 * and a future edit cannot quietly make the map even again.
 *
 * A warning about testing, because it is counter-intuitive and it is how this
 * class of bug hides: **a rotation round-trip cannot detect a handedness
 * error.** Conjugation by `M` and by `-M` are the same map on SO(3), and
 * `-diag(-1,1,1) = diag(1,-1,-1) = Rx(180)` is a proper rotation — so the
 * rotation half of this map is an inner automorphism, indistinguishable from an
 * ordinary 180° turn. The determinant lives *entirely* in the position leg and
 * in the mesh vertices. Any check that converts a rotation out and back will
 * pass just as happily against a wrong map. Test positions, and test chirality.
 *
 * `M` is not a choice the web port gets to make. It is Unity's own map, taken
 * from the vendored glTFast importer the desktop client uses on every model
 * (`ThirdParty/glTFast/Runtime/Scripts/NodeExtension.cs:64-88`): it negates
 * `translation.x`, maps rotation to `(x, -y, -z, w)`, and leaves scale alone.
 * The web reads the same S3 bytes through three's `GLTFLoader`, which applies
 * no conversion at all — so `M` is exactly what reconciles the two clients, and
 * it is also the inverse of what Unity did on the way in.
 *
 * ## Why one function serves both directions
 *
 * `M` is an involution (`M · M = I`) in all four component forms, so
 * Unity→three and three→Unity are the SAME function. That is deliberate and
 * load-bearing: the bug this module replaced was born from two conversions,
 * written in different places, that were each individually defensible and
 * cancelled each other. There is nothing here to get backwards.
 *
 * The runtime adapter still exposes directional names for readable call sites;
 * they are aliases of one implementation, not second copies.
 *
 * ## The quaternion and euler rules are derived, not guessed
 *
 * Conjugating a rotation by an orthogonal `M` maps
 * `Rot(axis, θ) → Rot(M·axis, θ · det(M))`. With `det(M) = -1` the sense of the
 * rotation reverses, so `Rot(a, θ) → Rot(M·a, -θ)`. Writing `q = (sin(θ/2)·a,
 * cos(θ/2))` and substituting gives
 *
 *   `(x, y, z, w) → (x, -y, -z, w)`
 *
 * — i.e. negate the components of the axes that are NOT flipped, and keep `w`.
 * (It matches glTFast's rotation line exactly, which is the cross-check.)
 *
 * For euler triples, the same substitution on each factor gives
 * `M·Rx(θ)·M = Rx(θ)` (axis and angle both flip, cancelling),
 * `M·Ry(θ)·M = Ry(-θ)` and `M·Rz(θ)·M = Rz(-θ)`. Since
 * `M(AB)M = (MAM)(MBM)`, Unity's ZXY composition `Ry(y)·Rx(x)·Rz(z)` becomes
 * `Ry(-y)·Rx(x)·Rz(-z)` — the same composition on euler `(x, -y, -z)`.
 *
 * A consequence worth knowing before "fixing" it: a spin about Unity +Y at
 * +60°/s becomes a spin about three +Y at -60°/s. The sign genuinely flips, and
 * the visible direction is unchanged — the two conventions disagree about which
 * sign means which way, which is what `M` is for.
 *
 * ## Unity rotation conventions reproduced here
 *
 * - `Quaternion.Euler(x, y, z)` composes **ZXY**: `q = Ry(y) · Rx(x) · Rz(z)`.
 * - `Quaternion.eulerAngles` returns degrees in **[0, 360)** and folds gimbal
 *   lock into Y (zeroing Z).
 *
 * {@link unityQuatToEuler} takes the output range explicitly rather than
 * defaulting, because the port is NOT uniform on this point and hiding it
 * behind a default is how the callers drifted apart in the first place. See
 * {@link DegreeRange}.
 */

import type { Vec3 } from "@models/data/entityData";

/** Unity-space quaternion `[x, y, z, w]` — same layout as `ParticipantQuat`. */
export type UnityQuat = readonly [number, number, number, number];

/**
 * Output range for a decomposed euler.
 *
 * - `"unity"` → `[0, 360)`, what `Quaternion.eulerAngles` returns.
 * - `"signed"` → `[-180, 180]`, reproducing the entity-snapshot path's existing
 *   wrap verbatim. Note the closed interval at BOTH ends is not a slip: the
 *   original folds `> 180` down and `< -180` up, so `-180` is a fixed point and
 *   survives. Tightening that to a half-open range would silently rewrite every
 *   persisted `-180` to `180`.
 *
 * Both describe the same rotation — `Quaternion.Euler` is periodic mod 360, so
 * Unity itself cannot tell them apart on read. The difference is visible only to
 * web-side consumers that show or compare the number, which is why entity
 * snapshots persist `"signed"` (the inspector's Rotation fields render the
 * stored value verbatim, so `"unity"` would turn a -10° part into 350°) while
 * every Unity-facing surface persists `"unity"`.
 */
export type DegreeRange = "unity" | "signed";

/**
 * `det(M)`. A basis change between opposite handednesses must be -1; assert on
 * this rather than on the individual signs, since it is the property that
 * actually matters and the one a well-meaning edit breaks.
 */
export const UNITY_BASIS_DETERMINANT = -1;

/** The diagonal of `M`, in order. Exported so a harness can check the product. */
export const UNITY_BASIS_DIAGONAL: Vec3 = [-1, 1, 1];

const K_DEG_TO_RAD = Math.PI / 180;
const K_RAD_TO_DEG = 180 / Math.PI;

/** Below this, `cos(pitch)` is treated as gimbal lock. Matches Unity's fold. */
const K_GIMBAL_EPSILON = 1e-6;

/** Collapse float noise like `359.99999999` → `0` so round-trips stay stable. */
const K_WRAP_EPSILON = 1e-7;

// ---------------------------------------------------------------------------
// The basis change. Each of these is its own inverse — see the module header.
// ---------------------------------------------------------------------------

/**
 * Convert a POSITION between Unity and three space (either direction).
 *
 * `scale` multiplies before the flip; it exists for the entity path, where a
 * root snapshot is stored in asset-scaled units.
 */
export function convertBasisPosition(v: Vec3, scale = 1): Vec3 {
  return [-v[0] * scale, v[1] * scale, v[2] * scale];
}

/**
 * Convert a DIRECTION (a normal, an axis, a forward vector) between Unity and
 * three space.
 *
 * Identical to {@link convertBasisPosition} because `M` is diagonal, so it
 * equals its own inverse-transpose and points and directions transform alike.
 * It is a separate export anyway: the equality is a property of THIS map, not a
 * general truth, and a call site that says "direction" documents that it
 * survived the check.
 */
export function convertBasisDirection(v: Vec3): Vec3 {
  return [-v[0], v[1], v[2]];
}

/**
 * Convert a NODE-FRAME rotation between Unity and three space (either
 * direction) — the pure basis conjugation `σ(R) = M R M`.
 *
 * `(x, y, z, w) → (x, -y, -z, w)`. Derived in the module header; note this is
 * NOT the plain component-wise `M` you would apply to a vector. A rotation axis
 * is a pseudovector, so it picks up the extra `det(M) = -1` that a direction
 * does not — which is exactly why {@link convertBasisDirection} negates only X
 * while this negates Y and Z.
 *
 * Use this when the object's local axes mean the same thing in both spaces —
 * an entity part's local rotation, or the cutting plane, whose local +Z is its
 * normal on both sides. Under `M` the converted rotation's local +Z equals
 * `M · (Unity forward)`, so a plane's visual forward and its converted normal
 * stay collinear AND same-signed.
 *
 * For a pose whose local −Z is treated as "forward" — every camera, avatar and
 * card — use {@link convertWorldPoseQuaternion} instead.
 *
 * Unlike its sibling, this one IS a group homomorphism (`σ(AB) = σ(A)σ(B)`), so
 * it is the only one of the two safe to use on relative rotations.
 */
export function convertBasisQuaternion(q: UnityQuat): UnityQuat {
  return [q[0], -q[1], -q[2], q[3]];
}

/**
 * Convert a FORWARD-CARRYING world pose between Unity and three space (either
 * direction): `σ(R) · Ry(180)`, in components `(x, y, z, w) → (z, w, x, y)`.
 *
 * ## Why this is not just {@link convertBasisQuaternion}
 *
 * Unity's convention is that an object looks down its local **+Z**; three's
 * `PerspectiveCamera` looks down its local **−Z**, and the mesh wrappers on this
 * path (avatar heads, device frames, name tags) are authored to that same
 * −Z-forward convention. That difference is a real convention bridge and has
 * nothing to do with handedness.
 *
 * Media-tile cards are the exception worth knowing about: their mesh is a
 * `PlaneGeometry`, so the readable face is local **+Z**. They still use this
 * function, because both their write leg (`surfaceAlignedTileEuler`) and their
 * read leg go through the same involution and therefore round-trip regardless —
 * see `mediaTileRenderer.ts`'s header.
 *
 * Today it is invisible, because the port's world map happens to be
 * `diag(-1, 1, -1)`, a PROPER rotation, and `diag(-1,1,-1) · ez = -ez` supplies
 * the bridge for free. The canonical `M` does not: `M · ez = +ez`. So moving the
 * world map to `M` without re-adding the bridge turns every camera, HMD head,
 * remote avatar and non-billboard card 180° around.
 *
 * Derivation: we need the converted pose's local −Z to be the converted
 * physical forward, i.e. `R'·(-ez) = M·(R·ez)`. Since `σ(R)·(M ez) = M R ez` and
 * `M ez = ez`, that gives `R'·(-ez) = σ(R)·ez`, hence `R' = σ(R)·Ry(180)`.
 * The Hamilton product `(x,-y,-z,w) ⊗ (0,1,0,0)` is `(z, w, x, y)`.
 *
 * ## Two properties worth knowing
 *
 * - It is **self-inverse** (`(x,y,z,w) → (z,w,x,y)` applied twice is the
 *   identity), so like everything else here one function serves both directions.
 * - It is **NOT a homomorphism** — the trailing `Ry(180)` does not cancel across
 *   a product. Convert whole world poses at the boundary; never use it to
 *   convert a relative rotation or to compose two converted rotations.
 */
export function convertWorldPoseQuaternion(q: UnityQuat): UnityQuat {
  return [q[2], q[3], q[0], q[1]];
}

/**
 * Convert a ZXY euler triple (degrees) between Unity and three space.
 *
 * `(x, y, z) → (x, -y, -z)`. The result is NOT normalized — normalize at the
 * point you persist it, with the range that surface has committed to.
 */
export function convertBasisEulerDegrees(e: Vec3): Vec3 {
  return [e[0], -e[1], -e[2]];
}

/**
 * Convert a SCALE between Unity and three space.
 *
 * The identity. `M` is a similarity on each axis, so a scale triple means the
 * same thing in both spaces. Exported so a call site can say the conversion was
 * considered — and so this file is the one place that would change if the map
 * ever stopped being diagonal.
 */
export function convertBasisScale(s: Vec3): Vec3 {
  return [s[0], s[1], s[2]];
}

// ---------------------------------------------------------------------------
// Unity rotation math (ZXY), pure.
// ---------------------------------------------------------------------------

/** Unity `Quaternion.Euler(x, y, z)` — composes `Ry(y) · Rx(x) · Rz(z)`. */
export function unityEulerToQuat(euler: Vec3): UnityQuat {
  const hx = (euler[0] * K_DEG_TO_RAD) / 2;
  const hy = (euler[1] * K_DEG_TO_RAD) / 2;
  const hz = (euler[2] * K_DEG_TO_RAD) / 2;
  const qx: UnityQuat = [Math.sin(hx), 0, 0, Math.cos(hx)];
  const qy: UnityQuat = [0, Math.sin(hy), 0, Math.cos(hy)];
  const qz: UnityQuat = [0, 0, Math.sin(hz), Math.cos(hz)];
  return quatMultiply(quatMultiply(qy, qx), qz);
}

/**
 * Unity `Quaternion.eulerAngles` — the inverse of {@link unityEulerToQuat}.
 *
 * `range` is required rather than defaulted; see {@link DegreeRange} for why the
 * port is not uniform on it.
 */
export function unityQuatToEuler(q: UnityQuat, range: DegreeRange): Vec3 {
  const [x, y, z, w] = q;
  // Rotation-matrix elements needed for the ZXY extraction.
  const m12 = 2 * (y * z - w * x); // -sin(pitch)
  const m10 = 2 * (x * y + w * z);
  const m11 = 1 - 2 * (x * x + z * z);
  const m02 = 2 * (x * z + w * y);
  const m22 = 1 - 2 * (x * x + y * y);

  const sinPitch = clamp(-m12, -1, 1);
  const ex = Math.asin(sinPitch);
  let ey: number;
  let ez: number;
  if (Math.abs(Math.cos(ex)) > K_GIMBAL_EPSILON) {
    ey = Math.atan2(m02, m22);
    ez = Math.atan2(m10, m11);
  } else {
    // Gimbal lock: Y and Z become degenerate, so fold the one surviving degree
    // of freedom into Y and zero Z, like Unity (and like three's own YXZ
    // extractor). Zeroing BOTH — which the old snapshot decomposer did — throws
    // that DOF away and silently loses the rotation.
    //
    // The `sinPitch` factor is load-bearing and is easy to drop: at pitch -90
    // the surviving combination is `y + z`, but at +90 it is `y - z`, so the
    // sign of the recovered angle follows the sign of the pitch. Without it the
    // +90 case decomposes to a DIFFERENT rotation (verified: recomposition
    // error ~0.5-2.0, i.e. not a wrap, an actual mismatch) — which is the
    // orientation of a horizontal cutting plane, the most common one there is.
    const m01 = 2 * (x * y - w * z);
    const m00 = 1 - 2 * (y * y + z * z);
    ey = Math.atan2(Math.sign(sinPitch) * m01, m00);
    ez = 0;
  }
  return [
    normalizeDegrees(ex * K_RAD_TO_DEG, range),
    normalizeDegrees(ey * K_RAD_TO_DEG, range),
    normalizeDegrees(ez * K_RAD_TO_DEG, range),
  ];
}

/** Hamilton product, Unity component order. */
export function quatMultiply(a: UnityQuat, b: UnityQuat): UnityQuat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by + ay * bw + az * bx - ax * bz,
    aw * bz + az * bw + ax * by - ay * bx,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/** Rotate a vector by a quaternion (`q * v * q⁻¹`, expanded). */
export function rotateVecByQuat(v: Vec3, q: UnityQuat): Vec3 {
  const [qx, qy, qz, qw] = q;
  const [vx, vy, vz] = v;
  // t = 2 * cross(q.xyz, v)
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  return [
    vx + qw * tx + (qy * tz - qz * ty),
    vy + qw * ty + (qz * tx - qx * tz),
    vz + qw * tz + (qx * ty - qy * tx),
  ];
}

/** Wrap degrees into `range`. */
export function normalizeDegrees(value: number, range: DegreeRange): number {
  const wrapped = value % 360;
  if (range === "signed") {
    let result = wrapped;
    if (result > 180) result -= 360;
    if (result < -180) result += 360;
    return Math.abs(result) < K_WRAP_EPSILON ? 0 : result;
  }
  const positive = wrapped < 0 ? wrapped + 360 : wrapped;
  return Math.abs(positive - 360) < K_WRAP_EPSILON ? 0 : positive;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
